import React, { useEffect } from 'react';
import { Button } from '@mui/material';
import { useBannerDismissal } from '../hooks/useBannerDismissal';
import { DismissibleBannerProps } from '../types';
import { DISMISSAL_DURATIONS } from '../constants/dismissalDurations';

interface BannerWithDismissalProps extends DismissibleBannerProps {
  children: (props: {
    isDismissed: boolean;
    onDismiss: () => void;
    actionButton?: React.ReactNode;
  }) => React.ReactNode;
}

const BannerWithDismissal: React.FC<BannerWithDismissalProps> = ({
  persistent = false,
  storageKey,
  dismissalDuration = DISMISSAL_DURATIONS.ONE_DAY,
  buttonText,
  autoHide,
  onDismiss,
  children,
  ...props
}) => {
  const isDismissible = !persistent;
  
  const { isDismissed, isLoading, dismiss } = useBannerDismissal({
    storageKey: storageKey || '',
    dismissalDurationMs: dismissalDuration,
    persistDismissal: isDismissible && !!storageKey,
  });

  const handleDismiss = () => {
    dismiss();
    onDismiss?.();
  };

  useEffect(() => {
    if (autoHide && !isDismissed && isDismissible && !isLoading) {
      const timer = setTimeout(handleDismiss, autoHide);
      return () => clearTimeout(timer);
    }
  }, [autoHide, isDismissed, isDismissible, isLoading]);

  if (isLoading) {
    return null;
  }

  const actionButton = buttonText && isDismissible ? (
    <Button
      variant="contained"
      size="medium"
      onClick={handleDismiss}
      sx={{ 
        textTransform: 'none',
        backgroundColor: '#000000',
        color: '#FFFFFF',
        borderRadius: 2,
        paddingX: 2,
        paddingY: 1,
        fontFamily: 'Mullish, Arial, sans-serif',
        fontWeight: 'medium',
        '&:hover': {
          backgroundColor: '#333333',
        },
      }}
    >
      {buttonText}
    </Button>
  ) : undefined;

  return (
    <>
      {children({
        isDismissed,
        onDismiss: handleDismiss,
        actionButton,
      })}
    </>
  );
};

export default BannerWithDismissal; 